import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Notification from '../components/ui/Notification';
import { getTranscriptionLanguageName } from '../constants/transcriptionLanguages';
import ReportEditor from '../features/sessions/report/components/ReportEditor';
import { useEditableReport } from '../features/sessions/report/hooks/useEditableReport';

const LoadingScreen = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading report editor...</p>
    </div>
  </div>
);

const ErrorScreen = ({ message, onBack }) => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="text-center max-w-md">
      <div className="text-red-600 text-6xl mb-4">⚠️</div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
      <p className="text-gray-600 mb-4">{message}</p>
      <button
        onClick={onBack}
        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
      >
        Back to Sessions
      </button>
    </div>
  </div>
);

const editorTitle = (session, lang) => {
  const title = session?.title || 'Report Editor';
  return lang !== 'en' ? `${title} (${getTranscriptionLanguageName(lang)})` : title;
};

export default function ReportEditPage() {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [notif, setNotif] = useState(null);
  const { session, report, loading, error, isSaving, saveErrors, currentLang, save } = useEditableReport(
    sessionId, searchParams.get('lang'), setNotif,
  );

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} onBack={() => navigate('/sessions')} />;

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Notification
        type={notif?.type}
        title={notif?.title}
        message={notif?.message}
        onClose={() => setNotif(null)}
      />

      <ReportEditor
        sessionId={sessionId}
        sessionTitle={editorTitle(session, currentLang)}
        initialData={report}
        onSave={save}
        onCancel={() => navigate(`/sessions/${sessionId}`)}
        isSaving={isSaving}
        saveErrors={saveErrors}
      />
    </div>
  );
}
